import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Logo } from '../logo';

interface NavWrapperProps<T> {
  items: T[];
  DesktopItemRenderer: React.ComponentType<{ item: T }>;
  MobileItemRenderer: React.ComponentType<{ item: T; onClick: () => void }>;
  RightNav: React.ReactNode;
  isDemo?: boolean;
}

/**
 * Generic navigation wrapper providing background, logo, branding, and mobile menu.
 * Takes a type parameter T for navigation items and renderer components.
 * Used by both MarketingNav and AppNavBar.
 */
export const NavWrapper = <T,>({
  items,
  DesktopItemRenderer,
  MobileItemRenderer,
  RightNav,
  isDemo = false,
}: NavWrapperProps<T>) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleItemClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="py-4 px-4 bg-primary-900 flex justify-between items-center shadow-medium">
        <div className="flex items-center gap-2">
          <div className="flex items-center mr-2">
            <Logo />
            <span className="ml-2 font-family-heading font-light text-2xl text-white border-b-2 border-transparent pt-1">
              Orderbot
            </span>
            {isDemo && (
              <span className="ml-2 mt-0.5 rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-950">
                Demo
              </span>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex pl-2 gap-x-6">
            {items.map((item, index) => (
              <DesktopItemRenderer key={index} item={item} />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Desktop Right Content */}
          <div className="hidden lg:flex items-center gap-3">{RightNav}</div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 text-gray-200 hover:text-white hover:bg-primary-800 rounded-md transition-colors duration-200"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
            >
              <span className="sr-only">{mobileMenuOpen ? 'Close menu' : 'Open menu'}</span>
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-16 left-0 right-0 bg-primary-900 border-primary-700 border-t z-50">
          <div className="px-2 pb-4 pt-2">
            {items.map((item, index) => (
              <MobileItemRenderer key={index} item={item} onClick={handleItemClick} />
            ))}
            <div className="border-t border-primary-700 mt-4 pt-4 space-y-2">{RightNav}</div>
          </div>
        </div>
      )}
    </>
  );
};
